import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Copy, Crown, ExternalLink, Eye, Link2, Loader2, Share2, Unlink } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { shareUrl } from '@/lib/bracket/shareApi';

const SOCIAL = [
  ['facebook', 'Facebook', (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`],
  ['x', 'X', (u, t) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}`],
  ['whatsapp', 'WhatsApp', (u, t) => `https://wa.me/?text=${encodeURIComponent(`${t} ${u}`)}`],
];

const FreeNote = ({ onUpgrade }) => (
  <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="share-free-note">
    <Crown className="w-4 h-4 text-amber-500 shrink-0" />
    <span>On the Free plan the shared bracket shows a faint Preview watermark and visitors can't print it.{' '}
      <button type="button" onClick={onUpgrade} className="font-semibold text-[#CF142B] underline" data-testid="share-upgrade-link">Upgrade to Pro</button> for a clean, printable link.</span>
  </div>
);

const SignIn = ({ templateId }) => {
  const back = encodeURIComponent(`/bracket-library?template=${templateId}&action=share`);
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-slate-600">Sign in or create a free account to get a link. Your bracket stays saved in this browser while you do.</p>
      <div className="grid grid-cols-2 gap-2">
        <Link to={`/login?redirect=${back}`}><Button variant="outline" className="w-full" data-testid="share-signin-btn">Sign in</Button></Link>
        <Link to={`/login?mode=register&redirect=${back}`}><Button className="w-full bg-[#00247D] hover:bg-[#001A5C]" data-testid="share-register-btn">Create free account</Button></Link>
      </div>
    </div>
  );
};

const LinkBox = ({ share, title, status, onStop }) => {
  const [confirm, setConfirm] = useState(false);
  const url = shareUrl(share.id);
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success('Link copied')).catch(() => toast.error('Copy failed, select the link and copy it'));
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <Input readOnly value={url} onFocus={(e) => e.target.select()} className="font-mono text-xs" data-testid="share-link-input" />
        <Button onClick={copy} className="bg-[#00247D] hover:bg-[#001A5C] shrink-0" data-testid="share-copy-btn"><Copy className="w-4 h-4 mr-1.5" />Copy</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <a href={url} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" data-testid="share-open-btn"><ExternalLink className="w-4 h-4 mr-1.5" />Open</Button></a>
        {navigator.share && (
          <Button variant="outline" size="sm" onClick={() => navigator.share({ title, url }).catch(() => {})} data-testid="share-native-btn"><Share2 className="w-4 h-4 mr-1.5" />Share</Button>
        )}
        {SOCIAL.map(([k, label, href]) => (
          <a key={k} href={href(url, title)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" data-testid={`share-${k}-btn`}>{label}</Button></a>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
        <span className="flex items-center gap-1.5" data-testid="share-live-status">
          <span className={`w-2 h-2 rounded-full ${status === 'error' ? 'bg-[#CF142B]' : 'bg-emerald-500 animate-pulse'}`} />
          {status === 'saving' ? 'Saving your latest changes…' : status === 'error' ? 'Last change not saved yet, it will retry' : 'Live: updates as you edit'}
        </span>
        <span className="flex items-center gap-1" data-testid="share-views"><Eye className="w-3.5 h-3.5" />{share.views || 0} views</span>
      </div>
      <Button variant="ghost" size="sm" className="self-start text-slate-500 hover:text-[#CF142B]" onClick={() => (confirm ? onStop() : setConfirm(true))} data-testid="share-stop-btn">
        <Unlink className="w-4 h-4 mr-1.5" />{confirm ? 'Click again to stop sharing' : 'Stop sharing'}
      </Button>
    </div>
  );
};

export const ShareDialog = ({ open, onOpenChange, signedIn, isPro, share, status, title, templateId, onCreate, onStop, onUpgrade }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-lg rounded-xl outline-none focus:outline-none focus-visible:outline-none" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="bracket-share-dialog">
      <DialogHeader className="text-left">
        <DialogTitle className="text-2xl font-bold text-[#00247D] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
          <Link2 className="w-5 h-5 text-[#CF142B]" />SHARE YOUR BRACKET
        </DialogTitle>
        <DialogDescription>
          Post the draw for teams and fans. Anyone with the link can view and zoom it{isPro ? ', and print or download it' : ''}. The link always shows your latest changes.
        </DialogDescription>
      </DialogHeader>
      {!signedIn ? <SignIn templateId={templateId} /> : share ? (
        <LinkBox share={share} title={title} status={status} onStop={onStop} />
      ) : (
        <Button onClick={onCreate} disabled={status === 'saving'} className="h-11 bg-[#CF142B] hover:bg-[#A81022] font-bold" data-testid="share-create-btn">
          {status === 'saving' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}Create share link
        </Button>
      )}
      {signedIn && !isPro && <FreeNote onUpgrade={onUpgrade} />}
    </DialogContent>
  </Dialog>
);
